import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Phone, MessageCircle, ArrowRight, Check, X, Plus, Pencil, Loader2, Image as ImageIcon, MessageSquareText } from "lucide-react";
import { api, fmt, errMsg, waLink, telLink, getToken, API, eventTypes, fmtRange } from "@/lib/api";
import { STATUS } from "@/pages/admin/Bookings";

export default function BookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [approve, setApprove] = useState({ final_price: "", deposit_required: "" });
  const [payment, setPayment] = useState("");
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({});
  const [showReceipt, setShowReceipt] = useState(false);

  const { data: b, refetch } = useQuery({
    queryKey: ["booking", id],
    queryFn: async () => (await api.get(`/admin/bookings/${id}`)).data,
    refetchInterval: 5000,
  });
  const { data: replies } = useQuery({ queryKey: ["replies-active"], queryFn: async () => (await api.get("/admin/replies")).data });

  useEffect(() => {
    if (!showReceipt) return;
    const k = (e) => { if (e.key === "Escape") setShowReceipt(false); };
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [showReceipt]);

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); refetch(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  if (!b) return <p className="text-muted2">جارٍ التحميل...</p>;

  const doApprove = () => act(async () => {
    if (!approve.final_price || !approve.deposit_required) throw new Error("أدخل القيمة والعربون");
    await api.post(`/admin/bookings/${id}/approve`, { final_price: Number(approve.final_price), deposit_required: Number(approve.deposit_required) });
  }, "تمت الموافقة على الطلب");

  const confirmDeposit = () => act(() => api.post(`/admin/bookings/${id}/confirm-deposit`), "تم تثبيت الحجز");
  const addPayment = () => act(async () => {
    if (!payment) throw new Error("أدخل مبلغ الدفعة");
    await api.post(`/admin/bookings/${id}/payments`, { amount: Number(payment) }); setPayment("");
  }, "تم تسجيل الدفعة");
  const cancel = () => { if (window.confirm("تأكيد إلغاء الحجز؟ العربون غير قابل للاسترداد.")) act(() => api.post(`/admin/bookings/${id}/cancel`), "تم إلغاء الحجز"); };
  const saveEdit = () => act(async () => {
    await api.patch(`/admin/bookings/${id}`, { ...edit, guests: edit.guests ? Number(edit.guests) : b.guests });
    setEditing(false);
  }, "تم تعديل الحجز");

  const startEdit = () => { setEdit({ name: b.name, phone: b.phone, event_type: b.event_type, guests: b.guests, date: b.date, start_time: b.start_time || "18:00", end_time: b.end_time || "22:00", notes: b.notes }); setEditing(true); };
  const receiptUrl = b.receipt_file_id ? `${API}/admin/files/${b.receipt_file_id}?auth=${getToken()}` : null;

  return (
    <div className="space-y-4 pb-6" data-testid="booking-detail">
      <button onClick={() => navigate(-1)} className="text-gold flex items-center gap-1 text-sm"><ArrowRight size={18} /> رجوع</button>

      <div className="card-lux p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="font-display text-2xl">{b.name}</h1>
            {b.booking_number && <p className="gold-text text-sm">{b.booking_number}</p>}
          </div>
          <span className={`text-xs px-3 py-1.5 rounded-full ${STATUS[b.status]?.cls}`}>{STATUS[b.status]?.label}</span>
        </div>
        <div className="flex gap-2 mb-4">
          <a href={telLink(b.phone)} data-testid="call-btn" className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full glass text-sm hover:bg-navy3 transition-colors"><Phone size={16} className="text-gold" /> اتصال</a>
          <a href={waLink(b.phone)} target="_blank" rel="noreferrer" data-testid="wa-btn" className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full glass text-sm hover:bg-navy3 transition-colors"><MessageCircle size={16} className="text-gold" /> واتساب</a>
        </div>

        {!editing ? (
          <div className="space-y-2 text-sm">
            <Row k="الهاتف" v={b.phone} />
            <Row k="المكان" v={b.venue_name} />
            <Row k="التاريخ" v={b.date} />
            <Row k="الوقت" v={fmtRange(b.start_time, b.end_time)} />
            <Row k="نوع المناسبة" v={b.event_type} />
            <Row k="عدد الضيوف" v={b.guests} />
            <Row k="الباقة" v={b.package_snapshot?.name} />
            {b.addons?.length > 0 && <Row k="الإضافات" v={b.addons.map((a) => a.name).join("، ")} />}
            {b.notes && <Row k="ملاحظات" v={b.notes} />}
            <Row k="المجموع التقديري" v={fmt(b.base_total)} />
            <div className="gold-divider my-2" />
            <Row k="قيمة العقد" v={fmt(b.final_price)} gold />
            <Row k="المدفوع" v={fmt(b.total_paid)} />
            <Row k="المتبقي" v={fmt(b.remaining)} />
            {b.confirmed_by && <Row k="أكّد الحجز" v={b.confirmed_by} />}
            {b.cancelled_by && <Row k="ألغى الحجز" v={b.cancelled_by} />}
            {b.status !== "cancelled" && (
              <button onClick={startEdit} data-testid="edit-btn" className="text-gold text-sm flex items-center gap-1 mt-2"><Pencil size={14} /> تعديل البيانات</button>
            )}
          </div>
        ) : (
          <div className="space-y-3" data-testid="edit-form">
            <Inp label="الاسم" val={edit.name} on={(v) => setEdit({ ...edit, name: v })} />
            <Inp label="الهاتف" val={edit.phone} on={(v) => setEdit({ ...edit, phone: v })} />
            <div>
              <label className="text-xs text-muted2 mb-1 block">نوع المناسبة</label>
              <select value={edit.event_type} onChange={(e) => setEdit({ ...edit, event_type: e.target.value })} className="w-full card-lux p-2.5 bg-navy2 text-offwhite outline-none">
                {eventTypes.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <Inp label="عدد الضيوف" val={edit.guests} on={(v) => setEdit({ ...edit, guests: v })} />
            <Inp label="التاريخ" val={edit.date} on={(v) => setEdit({ ...edit, date: v })} type="date" />
            <div className="grid grid-cols-2 gap-2">
              <Inp label="من الساعة" val={edit.start_time} on={(v) => setEdit({ ...edit, start_time: v })} type="time" />
              <Inp label="إلى الساعة" val={edit.end_time} on={(v) => setEdit({ ...edit, end_time: v })} type="time" />
            </div>
            <Inp label="ملاحظات" val={edit.notes} on={(v) => setEdit({ ...edit, notes: v })} />
            <div className="flex gap-2">
              <button onClick={saveEdit} disabled={busy} className="btn-gold flex-1 py-2.5 rounded-full text-sm">حفظ</button>
              <button onClick={() => setEditing(false)} className="flex-1 py-2.5 rounded-full border border-muted2/40 text-muted2 text-sm">إلغاء</button>
            </div>
          </div>
        )}
      </div>

      {/* Actions by status */}
      {b.status === "pending" && (
        <div className="card-lux p-5 space-y-3" data-testid="approve-panel">
          <h2 className="text-sm text-gold">الموافقة على الطلب وتحديد المبالغ</h2>
          <Inp label="قيمة الاتفاق النهائية" testid="approve-final-price" val={approve.final_price} on={(v) => setApprove({ ...approve, final_price: v.replace(/[^0-9]/g, "") })} type="tel" />
          <Inp label="مبلغ العربون المطلوب" testid="approve-deposit" val={approve.deposit_required} on={(v) => setApprove({ ...approve, deposit_required: v.replace(/[^0-9]/g, "") })} type="tel" />
          <button onClick={doApprove} disabled={busy} data-testid="approve-submit" className="btn-gold w-full py-3 rounded-full flex items-center justify-center gap-2">
            {busy && <Loader2 className="animate-spin" size={16} />} <Check size={16} /> الموافقة على طلب الحجز
          </button>
        </div>
      )}

      {(b.status === "approved" || b.status === "receipt_uploaded") && (
        <div className="card-lux p-5 space-y-3" data-testid="confirm-panel">
          <Row k="العربون المطلوب" v={fmt(b.deposit_required)} gold />
          <a href={waLink(b.phone, `مرحباً ${b.name} 🤍\nتمت الموافقة على طلب حجزك في قمرية.\nلتثبيت الموعد يرجى تحويل العربون (${fmt(b.deposit_required)}) ورفع صورة الوصل من هنا:\n${window.location.origin}/status/${b.id}`)}
            target="_blank" rel="noreferrer" data-testid="approval-wa-btn"
            className="w-full py-2.5 rounded-full glass text-sm flex items-center justify-center gap-2 hover:bg-navy3 transition-colors">
            <MessageCircle size={16} className="text-gold" /> إرسال رابط الدفع عبر واتساب
          </a>
          {receiptUrl ? (
            <button onClick={() => setShowReceipt(true)} data-testid="view-receipt-btn" className="w-full py-2.5 rounded-full glass text-sm flex items-center justify-center gap-2"><ImageIcon size={16} className="text-gold" /> مشاهدة وصل التحويل</button>
          ) : (
            <p className="text-muted2 text-xs text-center">لم يرفع الزبون الوصل بعد.</p>
          )}
          <button onClick={confirmDeposit} disabled={busy} data-testid="confirm-deposit-btn" className="btn-gold w-full py-3 rounded-full flex items-center justify-center gap-2">
            {busy && <Loader2 className="animate-spin" size={16} />} تم استلام المبلغ — تثبيت الحجز
          </button>
        </div>
      )}

      {b.status === "confirmed" && (
        <div className="card-lux p-5 space-y-3" data-testid="payment-panel">
          <h2 className="text-sm text-gold">إضافة دفعة</h2>
          <div className="flex gap-2">
            <input data-testid="payment-input" value={payment} onChange={(e) => setPayment(e.target.value.replace(/[^0-9]/g, ""))} placeholder="المبلغ" className="flex-1 card-lux p-2.5 bg-navy2 text-offwhite outline-none focus:ring-1 focus:ring-gold" />
            <button onClick={addPayment} disabled={busy} data-testid="add-payment-btn" className="btn-gold px-5 rounded-full text-sm flex items-center gap-1"><Plus size={16} /> إضافة</button>
          </div>
          {b.payments?.length > 0 && (
            <div className="space-y-1.5 pt-2">
              {b.payments.map((p) => (
                <div key={p.id} className="flex items-center justify-between text-xs text-muted2">
                  <span>{p.note} · {p.by_name}</span>
                  <span className="text-offwhite">{fmt(p.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {b.status === "confirmed" && (
        <div className="card-lux p-5 space-y-2" data-testid="wa-templates">
          <h2 className="text-sm text-gold flex items-center gap-1"><MessageSquareText size={15} /> قوالب واتساب سريعة</h2>
          {b.remaining > 0 && (
            <a href={waLink(b.phone, `مرحباً ${b.name} 🤍\nنذكّركم بالمبلغ المتبقي على حجزكم في قمرية: ${fmt(b.remaining)}.\nرقم الحجز: ${b.booking_number}.\nشكراً لكم 🤍`)} target="_blank" rel="noreferrer" data-testid="wa-remaining-btn" className="block text-sm text-offwhite/90 bg-navy p-3 rounded-xl hover:bg-navy3 transition-colors">💰 تذكير بالمبلغ المتبقي ({fmt(b.remaining)})</a>
          )}
          <a href={waLink(b.phone, `شكراً لاختياركم قمرية 🤍\nسعدنا بأن نكون جزءاً من ليلتكم الاستثنائية، ونتمنى لكم دوام الفرح والسعادة.\nنتشرف بخدمتكم دائماً ✨`)} target="_blank" rel="noreferrer" data-testid="wa-thankyou-btn" className="block text-sm text-offwhite/90 bg-navy p-3 rounded-xl hover:bg-navy3 transition-colors">🤍 رسالة شكر بعد المناسبة</a>
        </div>
      )}

      {/* Saved replies */}
      {replies?.filter((r) => r.active).length > 0 && (
        <div className="card-lux p-5 space-y-2" data-testid="replies-panel">
          <h2 className="text-sm text-gold flex items-center gap-1"><MessageSquareText size={15} /> ردود سريعة عبر واتساب</h2>
          {replies.filter((r) => r.active).map((r) => (
            <a key={r.id} href={waLink(b.phone, r.text)} target="_blank" rel="noreferrer" data-testid={`reply-${r.id}`}
              className="block text-sm text-offwhite/90 bg-navy p-3 rounded-xl hover:bg-navy3 transition-colors">{r.text}</a>
          ))}
        </div>
      )}

      {b.status !== "cancelled" && (
        <button onClick={cancel} disabled={busy} data-testid="cancel-btn" className="w-full py-3 rounded-full border border-red-500/40 text-red-400 text-sm flex items-center justify-center gap-2 hover:bg-red-500/10 transition-colors">
          <X size={16} /> إلغاء الحجز
        </button>
      )}

      {showReceipt && receiptUrl && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-5" onClick={() => setShowReceipt(false)} data-testid="receipt-modal">
          <img src={receiptUrl} alt="وصل التحويل" className="max-h-[85vh] max-w-full rounded-xl" />
        </div>
      )}
    </div>
  );
}

const Row = ({ k, v, gold }) => (
  <div className="flex items-center justify-between">
    <span className="text-muted2">{k}</span>
    <span className={gold ? "gold-text font-bold" : "text-offwhite"}>{v}</span>
  </div>
);
const Inp = ({ label, val, on, type = "text", testid }) => (
  <div>
    <label className="text-xs text-muted2 mb-1 block">{label}</label>
    <input data-testid={testid} type={type} value={val || ""} onChange={(e) => on(e.target.value)} className="w-full card-lux p-2.5 bg-navy2 text-offwhite outline-none focus:ring-1 focus:ring-gold" />
  </div>
);
